"use client";

import { useSupabase } from "@/components/providers/supabase-auth-provider";

export type TabVariant = "personal" | "public";

interface TabVariantFeatures {
	// Feature flags
	showBookmarkFilters: boolean;
	showFriendActivity: boolean;
	showAuthPrompts: boolean;
	showSimplifiedFilters: boolean;
	enableMyList: boolean;

	// UI text variations
	emptyStatePrefix: string;
	filterTitle: string;

	// Component behavior
	readOnlyCondition: (authState: boolean) => boolean;
}

/**
 * Hook to handle differences between personal and public festival tab views
 * This preserves the intentional design differences while allowing code reuse
 */
export const useTabVariant = (variant: TabVariant): TabVariantFeatures => {
	const { user } = useSupabase();
	const isAuthenticated = !!user;

	return {
		// Personal view: Shows bookmarks, friend activity, "My List" functionality
		// Public view: Discovery-focused, authentication prompts, simpler interface
		showBookmarkFilters: variant === "personal",
		showFriendActivity: variant === "personal",
		showAuthPrompts: variant === "public" && !isAuthenticated,
		showSimplifiedFilters: variant === "public",
		enableMyList: variant === "personal",

		// UI text variations
		emptyStatePrefix: variant === "personal" ? "Your" : "No",
		filterTitle: variant === "personal" ? "Filter" : "Show only saved",

		// Component behavior
		// Personal: read-only when NOT the profile owner
		// Public: read-only when NOT authenticated
		readOnlyCondition: (authState: boolean) => !authState,
	};
};

/**
 * Helper to determine if we should show internal controls
 * Used to maintain the existing pattern where components can be controlled externally
 */
export const shouldShowInternalControls = (
	onSearchChange?: (e: React.ChangeEvent<HTMLInputElement>) => void,
	onGenreChange?: (genre: string) => void,
): boolean => {
	return !onSearchChange && !onGenreChange;
};
